import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell from "./BuzzShell";

function fmtSec(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}″`;
  return `${Math.floor(s / 60)}′${String(s % 60).padStart(2, "0")}″`;
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bz-card" style={{ textAlign: "center", padding: 14, marginBottom: 0 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent || "#111827" }}>{value}</div>
      <div className="bz-hint">{label}</div>
    </div>
  );
}

export default function BuzzReports() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${config.API_BASE_URL}/buzz/reports`, { params: { days } })
      .then(r => setData(r.data)).catch(() => {});
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <BuzzShell><div className="bz-empty">…</div></BuzzShell>;

  const s = data.summary;
  const missedPct = s.totalCalls ? Math.round((s.missedCalls / s.totalCalls) * 100) : 0;
  const maxHourCalls = Math.max(1, ...data.byHour.map(h => h.calls));
  const totalFb = Object.values(data.ratingDistribution).reduce((a, b) => a + b, 0);

  return (
    <BuzzShell>
      <div className="bz-toolbar">
        {[7, 30, 90].map(d => (
          <button key={d} className={`bz-btn ${days === d ? "primary" : ""}`} onClick={() => setDays(d)}>
            {d} {t("days")}
          </button>
        ))}
      </div>

      <div className="bz-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", marginBottom: 14 }}>
        <Kpi label={t("Calls")} value={s.totalCalls} />
        <Kpi label={t("Missed calls")} value={`${s.missedCalls} (${missedPct}%)`}
             accent={s.missedCalls ? "#dc2626" : "#059669"} />
        <Kpi label={t("Avg response")} value={fmtSec(s.avgAckSec)} />
        <Kpi label={t("Avg completion")} value={fmtSec(s.avgDoneSec)} />
        <Kpi label={t("Rating")} value={s.avgOverall != null ? `${s.avgOverall}★` : "—"} />
        <Kpi label={t("Reviews")} value={s.feedbackCount} />
      </div>

      {data.byHour.length > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 10 }}>{t("Calls per hour")}</h4>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 120 }}>
            {Array.from({ length: 24 }, (_, h) => {
              const row = data.byHour.find(x => x.hour === h);
              const calls = row?.calls || 0;
              const missed = row?.missed || 0;
              return (
                <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}
                     title={`${h}:00 — ${calls} ${t("calls")}${missed ? `, ${missed} ${t("missed")}` : ""}`}>
                  <div style={{
                    height: `${(calls / maxHourCalls) * 100}%`,
                    background: missed ? "#dc2626" : "#d97706",
                    borderRadius: 3, minHeight: calls ? 3 : 0,
                  }} />
                  <div style={{ fontSize: 8, textAlign: "center", color: "#9ca3af" }}>{h}</div>
                </div>
              );
            })}
          </div>
          <p className="bz-hint" style={{ marginTop: 6 }}>
            🔶 {t("calls")} · 🟥 {t("hours with missed calls")}
          </p>
        </div>
      )}

      {data.byZone.length > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 8 }}>{t("By zone")}</h4>
          <table className="bz-table-list">
            <thead><tr>
              <th>{t("Zone")}</th><th>{t("Calls")}</th><th>{t("Missed")}</th>
              <th>{t("Avg response")}</th><th>{t("Avg completion")}</th>
            </tr></thead>
            <tbody>
              {data.byZone.map((z, i) => (
                <tr key={i}>
                  <td>{z.zone || <span className="bz-hint">—</span>}</td>
                  <td>{z.calls}</td>
                  <td style={{ color: z.missed ? "#dc2626" : undefined }}>{z.missed}</td>
                  <td>{fmtSec(z.avgAckSec)}</td>
                  <td>{fmtSec(z.avgDoneSec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalFb > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 10 }}>{t("Rating distribution")}</h4>
          {[5, 4, 3, 2, 1].map(r => {
            const c = data.ratingDistribution[r] || 0;
            return (
              <div key={r} style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
                <span style={{ width: 30, fontSize: 13 }}>{r}★</span>
                <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 4, height: 14 }}>
                  <div style={{ width: `${(c / totalFb) * 100}%`, background: "#d97706", height: "100%", borderRadius: 4 }} />
                </div>
                <span style={{ width: 30, fontSize: 13, textAlign: "right" }}>{c}</span>
              </div>
            );
          })}
        </div>
      )}

      {data.comments.length > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 8 }}>{t("Recent comments")}</h4>
          {data.comments.map((c, i) => (
            <div key={i} style={{ borderBottom: "1px solid #f3f4f6", padding: "8px 0" }}>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                {"⭐".repeat(c.rating_overall)} · {t("Table")} {c.label} · {new Date(c.created_at).toLocaleDateString("el-GR")}
              </div>
              <div style={{ fontSize: 14 }}>{c.comment}</div>
            </div>
          ))}
        </div>
      )}

      {s.totalCalls === 0 && (
        <div className="bz-empty">📊 {t("No data yet for this period.")}</div>
      )}
    </BuzzShell>
  );
}
